import { InventoryItem, AvailabilityReportItem, CounterpartyLink, SLBPosition } from '@/types';

const shouldShowTransfer = (value: string | undefined): boolean => {
  if (!value || value === '-' || value === '0' || value === '0.00') return false;
  const numValue = parseFloat(value);
  return !isNaN(numValue) && numValue !== 0;
};

export const shouldHideCorporateAction = (caStatus?: string | null): boolean => {
  if (!caStatus) return false;
  const normalizedStatus = caStatus.trim().toLowerCase();
  return normalizedStatus === 'deleted' || normalizedStatus === 'event error';
};

export const filterCorporateActionStatuses = (caStatus?: string | null): string | undefined => {
  if (!caStatus) return undefined;
  
  const statuses = caStatus.split(',')
    .map(s => s.trim())
    .filter(s => {
      if (!s) return false;
      const normalized = s.toLowerCase();
      return normalized !== 'deleted' && normalized !== 'event error';
    });
  
  return statuses.length > 0 ? statuses.join(', ') : undefined;
};

export const groupInventoryBySymbol = (items: InventoryItem[], benchmarkFeeThreshold: number = 4): AvailabilityReportItem[] => {
  const grouped = new Map<string, {
    symbol_id: string;
    sedol: string;
    isin: string;
    totalQuantity: number;
    totalPendingTransfers: number;
    benchmark_fee: number;
    ca: string;
    caStatuses: Set<string>;
    transfers: string;
    counterparties: Map<string, { quantity: number }>;
  }>();

  items.forEach((item) => {
    const benchmarkFee = item.benchmark_fee ? parseFloat(item.benchmark_fee) : 0;
    if (benchmarkFee < benchmarkFeeThreshold) return;

    const key = item.symbol_id;
    
    if (!grouped.has(key)) {
      const filteredStatus = filterCorporateActionStatuses(item.ca_status);
      const initialCA = (item.ca_type && filteredStatus) ? item.ca_type : '-';
      grouped.set(key, {
        symbol_id: item.symbol_id,
        sedol: item.sedol,
        isin: item.isin,
        totalQuantity: 0,
        totalPendingTransfers: 0,
        benchmark_fee: benchmarkFee,
        ca: initialCA,
        caStatuses: new Set(),
        transfers: shouldShowTransfer(item.pending_transfers) ? item.pending_transfers : '-',
        counterparties: new Map(),
      });
    }

    const group = grouped.get(key)!;
    group.totalQuantity += item.quantity || 0;
    group.benchmark_fee = Math.max(group.benchmark_fee, benchmarkFee);

    const pendingTransfers = parseFloat(item.pending_transfers || '0');
    if (!isNaN(pendingTransfers) && pendingTransfers > 0) {
      group.totalPendingTransfers += pendingTransfers;
    }

    if (item.counterparty) {
      const cpData = group.counterparties.get(item.counterparty) || { quantity: 0 };
      cpData.quantity += item.quantity || 0;
      group.counterparties.set(item.counterparty, cpData);
    }

    const filteredStatus = filterCorporateActionStatuses(item.ca_status);
    if (item.ca_type && item.ca_type !== '-' && filteredStatus) {
      group.ca = item.ca_type;
    }
    // Collect the symbol's CA *statuses* separately from the displayed type:
    // the CA Status filter matches these, same semantics as the Inventory
    // tab's filter (LR-597).
    if (filteredStatus) {
      filteredStatus.split(',').forEach((s) => group.caStatuses.add(s.trim()));
    }
    if (shouldShowTransfer(item.pending_transfers)) {
      group.transfers = item.pending_transfers!;
    }
  });

  return Array.from(grouped.values()).map((group) => {
    const counterpartyLinks: CounterpartyLink[] = Array.from(group.counterparties.entries()).map(([name, data]) => ({
      name,
      quantity: data.quantity,
      percentage: group.totalQuantity > 0 ? (data.quantity / group.totalQuantity) * 100 : 0,
    }));

    const adjustedQuantity = Math.max(0, group.totalQuantity - group.totalPendingTransfers);

    return {
      symbol_id: group.symbol_id,
      sedol: group.sedol,
      isin: group.isin,
      quantity: group.totalQuantity,
      pending_transfers: group.totalPendingTransfers,
      adjusted_quantity: adjustedQuantity,
      benchmark_fee: group.benchmark_fee,
      ca: group.ca,
      ca_statuses: Array.from(group.caStatuses).sort(),
      transfers: group.transfers,
      counterparties: counterpartyLinks,
    };
  });
};

export const transformInventoryItemToSLBPosition = (
  item: InventoryItem
): SLBPosition => {
  const filteredCAStatus = filterCorporateActionStatuses(item.ca_status);
  const shouldHideCA = !filteredCAStatus;
  const hasSettledBase = item.settled_quantity != null;

  return {
    legalEntity: item.legal_entity,
    accountId: item.account_id,
    counterparty: item.counterparty,
    type: item.type,
    sedol: item.sedol,
    isin: item.isin,
    symbolId: item.symbol_id,
    exchange: item.exchange,
    shortName: item.short_name,
    price: item.price ? parseFloat(item.price) : 0,
    quantity: item.quantity || 0,
    settledQuantity: item.settled_quantity ?? null,
    currency: item.currency,
    leverageRate: item.leverage_rate ? parseFloat(item.leverage_rate) : 0,
    valueEUR: item.value ? parseFloat(item.value) : 0,
    isLiquid: item.is_liquid,
    brokerAccountName: item.broker_account_name ?? '',
    brokerSettlementCounterparty: item.broker_settlement_counterparty ?? '',
    velocityLQuantity: item.velocity_l_quantity ?? undefined,
    velocityMQuantity: item.velocity_m_quantity ?? undefined,
    cimbSfQuantity: item.cimb_sf_quantity ?? undefined,
    cimbEq2Quantity: item.cimb_eq2_quantity ?? undefined,
    cimbSf3Quantity: item.cimb_sf3_quantity ?? undefined,
    exDate: shouldHideCA ? undefined : (item.ex_date || undefined),
    caType: shouldHideCA ? undefined : (item.ca_type || undefined),
    caStatus: filteredCAStatus,
    benchmarkFee: item.benchmark_fee ? parseFloat(item.benchmark_fee) : 0,
    quantityOnLoan: item.quantity_on_loan
      ? parseFloat(item.quantity_on_loan)
      : 0,
    activeUtilisation: item.active_utilisation
      ? parseFloat(item.active_utilisation)
      : 0,
    currencyBenchmark: item.currency_benchmark,
    rebateRate: item.rebate_rate ? parseFloat(item.rebate_rate) : 0,
    dailyPotentialEUR: item.daily_potential
      ? parseFloat(item.daily_potential)
      : 0,
    adtv30D: item.adtv_30d ? parseFloat(item.adtv_30d) : 0,
    adtvMultiple: item.adtv_multiple ? parseFloat(item.adtv_multiple) : 0,
    adtvAdjustedPotentialEUR: item.adtv_potential
      ? parseFloat(item.adtv_potential)
      : 0,
    // A pre-LR-615 snapshot has a stored ladder with no base behind it, computed
    // off trade-date and wrong by the whole unsettled volume — sign included on a
    // short. Drop it here, at the one point every consumer reads through: the cell
    // renderer alone would still leave the old numbers in CSV, the clipboard, the
    // sort order, the numeric filters and the range facet.
    quantityT1: hasSettledBase ? item.quantity_t1 ?? null : null,
    quantityT2: hasSettledBase ? item.quantity_t2 ?? null : null,
    quantityTx: hasSettledBase ? item.quantity_tx ?? null : null,
    pendingSettlementQuantity: item.pending_settlement_quantity ?? 0,
    pendingTransfers: item.pending_transfers ? parseFloat(item.pending_transfers) : 0,
    l2cpQuantity: item.l2cp_quantity ?? undefined,
    l2cpAmountEur: item.l2cp_amount_eur ?? undefined,
    l2cpRate: item.l2cp_rate ?? undefined,
    l2cpGrossRate: item.l2cp_gross_rate ?? undefined,
    l2cpSnpDelta: item.l2cp_snp_delta ?? undefined,
    loans: item.loans?.map(loan => ({
      accountName: loan.account_name,
      quantityLent: loan.quantity_lent,
      amountEUR: loan.amount_eur,
    })),
  };
};

